import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Mail, Send, ExternalLink } from "lucide-react";
import Footer from "@/components/Footer";
import { useLanguage } from '@/i18n/LanguageContext';

const WA_LINK = "https://wa.link/b5iddw";

const Contact = () => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsAppFallback, setShowWhatsAppFallback] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  const TOPICS = [
    { value: "General Inquiry", label: t('contact.generalInquiry') },
    { value: "Technical Support", label: t('contact.technicalSupport') },
    { value: "Billing & Subscription", label: t('contact.billingSubscription') },
    { value: "Feature Request", label: t('contact.featureRequest') },
    { value: "Bug Report", label: t('contact.bugReport') },
    { value: "Other", label: t('contact.other') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim() || !topic || !message.trim()) {
      toast({ title: t('contact.missingInfo'), description: t('contact.fillRequired'), variant: "destructive" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { toast({ title: t('contact.invalidEmail'), description: t('contact.validEmail'), variant: "destructive" }); return; }
    if (!consent) { toast({ title: t('contact.consentRequired'), description: t('contact.consentMsg'), variant: "destructive" }); return; }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert({
        full_name: fullName.trim(), email: email.trim(), phone: phone.trim() || null,
        company: company.trim() || null, topic, message: message.trim(), consent,
        source_url: window.location.href, user_agent: navigator.userAgent,
      });
      if (error) throw error;
      toast({ title: t('contact.success'), description: t('contact.openingWhatsApp') });
      setFullName(""); setEmail(""); setPhone(""); setCompany(""); setTopic(""); setMessage(""); setConsent(false);
      setTimeout(() => {
        const newWindow = window.open(WA_LINK, "_blank", "noopener,noreferrer");
        if (!newWindow || newWindow.closed) setShowWhatsAppFallback(true);
      }, 800);
    } catch (error: any) {
      console.error("Error submitting contact form:", error);
      toast({ title: t('contact.error'), description: t('contact.errorMsg'), variant: "destructive" });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 container mx-auto px-4 py-12 max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-2">{t('contact.title')}</h1>
          <p className="text-muted-foreground">{t('contact.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-card p-8 rounded-lg border">
          <div>
            <Label htmlFor="fullName">{t('contact.fullName')}</Label>
            <Input id="fullName" type="text" placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={100} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="email">{t('contact.email')}</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="phone">{t('contact.phone')}</Label>
            <Input id="phone" type="tel" placeholder="+1 (555) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={30} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="company">{t('contact.company')}</Label>
            <Input id="company" type="text" placeholder="Acme Inc." value={company} onChange={(e) => setCompany(e.target.value)} maxLength={100} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="topic">{t('contact.topic')}</Label>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger className="mt-2"><SelectValue placeholder={t('contact.selectTopic')} /></SelectTrigger>
              <SelectContent>
                {TOPICS.map((tp) => (<SelectItem key={tp.value} value={tp.value}>{tp.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="message">{t('contact.message')}</Label>
            <Textarea id="message" placeholder={t('contact.messagePlaceholder')} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={6} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">{message.length}/2000 {t('contact.characters')}</p>
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox id="consent" checked={consent} onCheckedChange={(checked) => setConsent(checked === true)} />
            <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">{t('contact.consent')}</Label>
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('contact.sending') : (<><Send className="w-4 h-4 mr-2" />{t('contact.sendMessage')}</>)}
          </Button>
          {showWhatsAppFallback && (
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="w-full">
              <Button type="button" variant="outline" className="w-full mt-3 border-green-500 text-green-600 hover:bg-green-50">
                <ExternalLink className="w-4 h-4 mr-2" />{t('contact.openWhatsApp')}
              </Button>
            </a>
          )}
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
